import { LoggerService } from "@/shared/services";

/**
 * Efficient coordinate storage for a single map path.
 * Uses typed arrays for zero-copy transfer from worker and fast binary search.
 */
export class MapPathData {
  private _latitudes: Float64Array = new Float64Array(0);
  private _longitudes: Float64Array = new Float64Array(0);
  private _logTimes: BigUint64Array = new BigUint64Array(0);
  private _isComplete = false;

  /** Current length (number of points loaded so far) */
  get length(): number {
    return this._latitudes.length;
  }

  /** Whether all data has been received */
  get isComplete(): boolean {
    return this._isComplete;
  }

  /** Append a chunk of coordinates (received from worker) */
  appendChunk(
    latitudes: Float64Array,
    longitudes: Float64Array,
    logTimes: BigUint64Array,
  ): void {
    // Grow flat arrays by copying old + new data
    const newLat = new Float64Array(this._latitudes.length + latitudes.length);
    newLat.set(this._latitudes, 0);
    newLat.set(latitudes, this._latitudes.length);
    this._latitudes = newLat;

    const newLon = new Float64Array(
      this._longitudes.length + longitudes.length,
    );
    newLon.set(this._longitudes, 0);
    newLon.set(longitudes, this._longitudes.length);
    this._longitudes = newLon;

    const newTime = new BigUint64Array(this._logTimes.length + logTimes.length);
    newTime.set(this._logTimes, 0);
    newTime.set(logTimes, this._logTimes.length);
    this._logTimes = newTime;
  }

  /** Mark loading as complete */
  markComplete(totalCount: number): void {
    this._isComplete = true;
    // Verify the count matches
    if (this._latitudes.length !== totalCount) {
      LoggerService.warn(
        `MapPathData: Expected ${totalCount} points, but have ${this._latitudes.length}`,
      );
    }
  }

  /** Get coordinate at index */
  getCoordinate(
    index: number,
  ): { lat: number; lon: number; logTime: bigint } | undefined {
    if (index < 0 || index >= this._latitudes.length) {
      return undefined;
    }
    return {
      lat: this._latitudes[index],
      lon: this._longitudes[index],
      logTime: this._logTimes[index],
    };
  }

  /** Binary search for nearest point by logTime (for playback) */
  findNearestIndex(targetLogTime: bigint): number {
    if (this._logTimes.length === 0) {
      return 0;
    }

    let left = 0;
    let right = this._logTimes.length - 1;

    // Handle edge cases
    if (targetLogTime <= this._logTimes[0]) {
      return 0;
    }
    if (targetLogTime >= this._logTimes[right]) {
      return right;
    }

    // Binary search for the closest index
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const midTime = this._logTimes[mid];

      if (midTime === targetLogTime) {
        return mid;
      }

      if (midTime < targetLogTime) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    // At this point, right < left
    // right is the largest index with time < targetLogTime
    // left is the smallest index with time > targetLogTime
    // Return the closer one
    if (left >= this._logTimes.length) {
      return right;
    }

    const leftDiff =
      this._logTimes[left] > targetLogTime
        ? this._logTimes[left] - targetLogTime
        : targetLogTime - this._logTimes[left];
    const rightDiff =
      this._logTimes[right] > targetLogTime
        ? this._logTimes[right] - targetLogTime
        : targetLogTime - this._logTimes[right];

    return leftDiff < rightDiff ? left : right;
  }

  /** Get all coordinates as GeoJSON-compatible array (for Mapbox LineString) */
  toGeoJSONCoordinates(): [number, number][] {
    const coords: [number, number][] = [];
    for (let i = 0; i < this._latitudes.length; i++) {
      // GeoJSON uses [lon, lat] order
      coords.push([this._longitudes[i], this._latitudes[i]]);
    }
    return coords;
  }
}
